import { createFileRoute, redirect, SplatParams, useParams } from '@tanstack/react-router';
import { IRouterContext } from '@my-pages/routing/types';
import {
    getGetUmbracoTafDeliveryApiV1ContentGlobalSiteSettingsQueryOptions,
    getGetUmbracoTafDeliveryApiV1ContentItemPathSuspenseQueryOptions,
    useGetUmbracoTafDeliveryApiV1ContentItemPathSuspense,
} from '@shared/integrations/umbraco-taf-delivery-api/features/content/content.ts';
import { AxiosError } from 'axios';
import { Helmet } from 'react-helmet-async';
import { SuspenseQueryErrorHandling } from '@shared/components/SuspenseQueryErrorHandling.tsx';
import FullScreenLoader from '@shared/components/FullScreenLoader.tsx';
import PageContentRenderer from '@shared/integrations/umbraco-utils/PageContentRenderer.tsx';

export const Route = createFileRoute('/_cms/$')({
    component: RouteComponent,
    loader: async ({ context, params }: { context: IRouterContext; params: SplatParams }) => {
        const queryOptions = getGetUmbracoTafDeliveryApiV1ContentItemPathSuspenseQueryOptions(params._splat ?? '/');
        queryOptions.retry = 1;
        await context.queryClient.ensureQueryData(queryOptions).catch(async (error) => {
            const isAxiosError = error instanceof AxiosError;
            if (!isAxiosError) throw error;
            const { loginUrl } = await context.queryClient.ensureQueryData(getGetUmbracoTafDeliveryApiV1ContentGlobalSiteSettingsQueryOptions());
            switch (error.response?.status) {
                case 401: {
                    const isAuthenticated = context.applicationStore.getState().isAuthenticated;
                    if (isAuthenticated) {
                        throw redirect({
                            to: '/utloggad',
                            search: {
                                returnUrl: location.pathname,
                            },
                        });
                    }
                    if (!loginUrl) return;
                    throw redirect({
                        to: '/$',
                        params: {
                            _splat: loginUrl,
                        },
                        search: {
                            returnUrl: location.pathname,
                        },
                    });
                }
            }
        });
    },
});

function RouteComponent() {
    const { _splat } = useParams({ strict: false });
    const { data } = useGetUmbracoTafDeliveryApiV1ContentItemPathSuspense(_splat ?? '/');

    return (
        <>
            <Helmet>
                <title>{data?.name} | Transport - Mina Sidor</title>
                <link
                    rel='canonical'
                    href={`${window.location.origin}${data?.route?.path}`}
                />
            </Helmet>
            <SuspenseQueryErrorHandling loader={<FullScreenLoader />}>
                <PageContentRenderer pageData={data} />
            </SuspenseQueryErrorHandling>
        </>
    );
}
